import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';

import { GetParticipantDataQuery as GetParticipantData, Permission } from '__generated__/graphql';

import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditMailOptInsMutation from './EditMailOptInsMutation';
import GetMailPermissionsQuery from '../../../Communication/MailPermissions/GetMailPermissionsQuery';
import ProtectedSection from '../../../Common/ProtectedSection';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

type Participant = GetParticipantData['project']['participant'];

export interface EditMailOptInsFormProps {
  participant: Participant;
}

const EditMailOptInsForm = ({ participant }: EditMailOptInsFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const { data, loading } = useQuery(
    GetMailPermissionsQuery, {
      variables: { id: project.id },
    },
  );

  const [editMailOptIn] = useMutation(
    EditMailOptInsMutation,
    {
      onCompleted: () => {
        notifier.success(t('notifications.contact_saved'));
      },
    },
  );

  const mailPermissions = data?.project.mail_permissions.filter((mailPermission) => !mailPermission.integration) || [];

  const grantedPermissions = participant.granted_mail_permissions.map(
    (permission) => permission.id,
  );

  const toggleMailPermission = (mailPermission: { id: string; title: string; }) => {
    const shouldOptIn = !grantedPermissions.includes(mailPermission.id);
    const mailOptIns = shouldOptIn ? [mailPermission.id] : [];
    const mailOptOuts = !shouldOptIn ? [mailPermission.id] : [];

    editMailOptIn({
      variables: {
        input: {
          participant_id: participant.id,
          mail_opt_ins: mailOptIns,
          mail_opt_outs: mailOptOuts,
        },
      },
      optimisticResponse: ({
        editMailOptIns: {
          id: participant.id,
          granted_mail_permissions: shouldOptIn
            ? sortBy([
              ...participant.granted_mail_permissions,
              {
                id: mailPermission.id,
                title: mailPermission.title,
                __typename: 'MailPermission',
              },
            ], 'title')
            : participant.granted_mail_permissions.filter(({ id }) => id !== mailPermission.id),
          __typename: 'Participant',
        },
      }),
    });
  };

  return (
    <UI.FormGrid>
      <UI.Div>
        <UI.InputDescription>
          {t('participant_mail_opt_ins_description')}
        </UI.InputDescription>
      </UI.Div>

      <UI.FormGrid sc={{ gutter: 0.5 }}>
        {loading && <UI.Loader />}

        {!loading && (
          <>
            {mailPermissions.map((mailPermission, index) => (
              <UI.GridContainer
                sc={{ columns: '3fr 1fr', alignVertical: 'center' }}
                key={mailPermission.id}
              >
                <UI.Div sc={{ muted: !grantedPermissions.includes(mailPermission.id) }}>
                  <UI.InputLabel sc={{ mb: 0 }}>
                    {mailPermission.title}
                  </UI.InputLabel>
                </UI.Div>
                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    checked={grantedPermissions.includes(mailPermission.id)}
                    onChange={() => toggleMailPermission(mailPermission)}
                    name={`mail_permissions.${index}`}
                    aria-label={t(grantedPermissions.includes(mailPermission.id) ? 'disable_name' : 'enable_name', {
                      name: mailPermission.title,
                    })}
                  />
                </UI.Div>
              </UI.GridContainer>
            ))}

            {mailPermissions.length === 0 && (
              <ProtectedSection permissions={[Permission.messages]}>
                <UI.Div>
                  <UI.NavButton to={`/${project.id}/communication/permissions`}>
                    {t('manage_mail_permissions')}
                  </UI.NavButton>
                </UI.Div>
              </ProtectedSection>
            )}
          </>
        )}
      </UI.FormGrid>
    </UI.FormGrid>
  );
};

export default EditMailOptInsForm;
